/**
 * **心跳：`net/` 里唯一的周期行为。**
 *
 * ⚠️ 它**不参与状态同步**。状态是房主推的（`docs/netcode-s3.md` §3）。
 * 心跳只回答两个问题：
 *
 * 1. **对端还在不在**（存活）
 * 2. **他有多快**（RTT —— 房主选举要用）
 *
 * ## 为什么不用 `old_repo` 的 RFC 6298
 *
 * 那套东西（srtt / rttvar / `rto = srtt + max(1, 4·rttvar)`）是**给应用层重传用的**。
 * 而 WebRTC 的 DataChannel 默认就是可靠有序的
 * （`pc.createDataChannel("data")`，无参数），Trystero 的 `request` 又自带
 * `timeoutMs`。也就是说：**我们没有应用层重传，那套估算就没有服务对象**。
 *
 * 留下的只有"平滑"这一个需求，所以用一行 EMA：
 *
 * ```
 * srtt ← srtt + alpha · (sample − srtt)
 * ```
 *
 * 要平滑的理由很实际：选举是拿 RTT 排序的，一次抖动不该把房主换掉。
 *
 * ## 判死是**可逆**的
 *
 * `old_repo` 的 `peerclose` 是一条单向的路。但 WebRTC 的断线检测本来就有
 * 延迟和抖动，把"判死"做成单向的，等于把一次网络抖动永久固化成一次掉线 ——
 * 而掉线是要暂停游戏的（Q11）。
 *
 * 所以：连续 `missThreshold` 次 ping 失败 → 判死（发一次 `down`）；
 * 之后**继续 ping**，通了就发一次 `up`。两个事件都只在**状态真的变化**时发。
 *
 * ⚠️ 一个容易漏的点：`Transport.ping` 的 `timeoutMs` 是**必须**的。
 * Trystero 的 `ping()` 对端不回 pong 时返回的是一个**永不落地**的 Promise，
 * 没有超时的话这个 tick 会永远挂着，心跳就死在了第一次失败上。
 */

export type RttRow = Readonly<Record<string, number>>;
export type PeerLiveness = 'up' | 'down';

export interface HeartbeatOptions {
  readonly ping: (peerId: string, timeoutMs: number) => Promise<number>;
  /** 当前在线的 peer id。心跳每 tick 重新取一次 —— 名册是别人的事 */
  readonly peers: () => readonly string[];
  /** 两次心跳的间隔。默认 3000ms */
  readonly intervalMs?: number;
  /** 连续失败几次算死。默认 3 */
  readonly missThreshold?: number;
  /** 单次 ping 的超时。必须**小于** interval，默认取 interval 的一半 */
  readonly pingTimeoutMs?: number;
  /** EMA 系数。默认 0.25 */
  readonly alpha?: number;
  /** 每 tick 结束时回调，参数是"我这一行" RTT。`S3Peer` 用它广播给全房 */
  readonly onTick?: (row: RttRow) => void;
  readonly onSample?: (peerId: string, rttMs: number, smoothedMs: number) => void;
  readonly onStateChange?: (peerId: string, state: PeerLiveness) => void;
  /** 观测用。心跳失败**不是**异常路径，这里只用来记日志 */
  readonly onMiss?: (peerId: string, misses: number, error: unknown) => void;
}

export interface Heartbeat {
  start(): void;
  stop(): void;
  readonly running: boolean;
  /** 立刻跑一次。**不要求 `start()` 过** —— 新 peer 进房时值得马上测一次 */
  tick(): Promise<void>;
  /** 把某个 peer 纳入心跳（重置它的 miss 计数与状态） */
  note(peerId: string): void;
  /** 忘掉某个 peer（他离开了） */
  forget(peerId: string): void;
  /** 平滑后的 RTT，还没测出来时为 `null` */
  rtt(peerId: string): number | null;
  alive(peerId: string): boolean;
  /** 我这一行：我到每个已知 peer 的 RTT。**没有样本的 peer 不会出现在里面** */
  row(): RttRow;
  /** 已知 peer 的存活状态 */
  states(): ReadonlyMap<string, PeerLiveness>;
}

interface PeerRecord {
  misses: number;
  srtt: number | null;
  state: PeerLiveness;
}

const DEFAULT_INTERVAL_MS = 3000;
const DEFAULT_MISS_THRESHOLD = 3;
const DEFAULT_ALPHA = 0.25;

export function createHeartbeat(options: HeartbeatOptions): Heartbeat {
  const intervalMs = options.intervalMs ?? DEFAULT_INTERVAL_MS;
  const missThreshold = options.missThreshold ?? DEFAULT_MISS_THRESHOLD;
  const pingTimeoutMs = options.pingTimeoutMs ?? Math.max(250, Math.floor(intervalMs / 2));
  const alpha = options.alpha ?? DEFAULT_ALPHA;

  const records = new Map<string, PeerRecord>();
  let timer: ReturnType<typeof setInterval> | null = null;
  let ticking = false;

  const note = (peerId: string): void => {
    const existing = records.get(peerId);
    if (existing === undefined) records.set(peerId, { misses: 0, srtt: null, state: 'up' });
  };

  const forget = (peerId: string): void => {
    records.delete(peerId);
  };

  const setState = (peerId: string, record: PeerRecord, state: PeerLiveness): void => {
    if (record.state === state) return;
    record.state = state;
    // 判死时把 RTT 丢掉：一个不在线的人的延迟不该参与房主选举
    if (state === 'down') record.srtt = null;
    options.onStateChange?.(peerId, state);
  };

  const pingOne = async (peerId: string): Promise<void> => {
    const record = records.get(peerId);
    if (record === undefined) return;
    try {
      const sample = await options.ping(peerId, pingTimeoutMs);
      record.misses = 0;
      record.srtt = record.srtt === null ? sample : record.srtt + alpha * (sample - record.srtt);
      setState(peerId, record, 'up');
      options.onSample?.(peerId, sample, record.srtt);
    } catch (error) {
      record.misses += 1;
      options.onMiss?.(peerId, record.misses, error);
      if (record.misses >= missThreshold) setState(peerId, record, 'down');
    }
  };

  const tick = async (): Promise<void> => {
    // 上一 tick 还没跑完就跳过这一 tick：心跳不该堆叠
    if (ticking) return;
    ticking = true;
    try {
      const live = options.peers();
      // 名册是**权威**的：不在里面的就走人。这样"离开"不需要额外的清理调用
      const liveSet = new Set(live);
      for (const peerId of [...records.keys()]) {
        if (!liveSet.has(peerId)) records.delete(peerId);
      }
      for (const peerId of live) note(peerId);
      await Promise.all(live.map((peerId) => pingOne(peerId)));
      options.onTick?.(row());
    } finally {
      ticking = false;
    }
  };

  function row(): RttRow {
    const out: Record<string, number> = {};
    for (const [peerId, record] of records) {
      if (record.srtt !== null) out[peerId] = Math.round(record.srtt * 100) / 100;
    }
    return out;
  }

  return {
    start() {
      if (timer !== null) return;
      timer = setInterval(() => {
        void tick();
      }, intervalMs);
      void tick();
    },
    stop() {
      if (timer === null) return;
      clearInterval(timer);
      timer = null;
    },
    get running() {
      return timer !== null;
    },
    tick,
    note,
    forget,
    rtt: (peerId) => records.get(peerId)?.srtt ?? null,
    alive: (peerId) => (records.get(peerId)?.state ?? 'up') === 'up',
    row,
    states() {
      const out = new Map<string, PeerLiveness>();
      for (const [peerId, record] of records) out.set(peerId, record.state);
      return out;
    },
  };
}
